import './SendAndTrack.scss';

import Debug from 'debug';
import classnames from 'classnames';
import React, { useCallback, useContext, useState } from 'react';
import { Button, LoadingDots } from '@bblabs/module-library/atoms';

import { SendAirplane } from '../SVGS/SendAirplane';
import { setErrorNotification } from '../abstractions/notifications';
import { isViewInReadMode } from '../abstractions/office';
import { OverlayCoordinatorContext } from '../coordinators/overlay';
import { getAuthenticatedApiService } from '../services/AuthenticatedApi';
import { MetricService } from '../services/MetricService';
import { userService } from '../services/UserService';
import { sendEmail } from '../services/bixel';
import { waitForErrorModal } from '../lib/wait';
import { EnvironmentContext, UserContext } from './App';

export type Props = {
  /** Flag indicating whether the Send Info Overlay should show or not. */
  showSendInfoOverlay: boolean;
  onSend?(): void;
};

const debug = Debug('o365:SendAndTrack');

/**
 * Handles the rendering and optional overlay of the "Send + Track" button/
 */
export function SendAndTrack({
  showSendInfoOverlay,
  onSend = () => {
    /* intentional */
  },
}: Props) {
  const env = useContext(EnvironmentContext);
  const user = useContext(UserContext);
  const overlayContext = useContext(OverlayCoordinatorContext);
  const [buttonDisabled, setButtonDisabled] = useState(false);

  const clientId = user.user?.get('clientId');

  const sendClick = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();

      /**
       * Allows the ERROR_SEND_FAIL to keep retrying based on user input
       */
      async function sendLoop(...params: Parameters<typeof sendEmail>): Promise<boolean> {
        debug('sendLoop()');
        const result = await sendEmail(...params);
        const success = !result.includes('fail');
        if (result === 'fail:send') {
          await waitForErrorModal('ERROR_SEND_FAIL', async () => sendLoop(...params));
        } else if (result === 'fail:track') {
          await waitForErrorModal('ERROR_TRACK_FAIL', () => {
            /* intentional, only has close button */
          });
        }
        return success;
      }

      overlayContext('dismissInfoOverlay');

      if (!clientId) {
        setErrorNotification('Unable to Send + Track due to an unknown error');
        return;
      }

      setButtonDisabled(true);
      try {
        MetricService.increment('click.task_pane.send_and_track.button');

        const api = getAuthenticatedApiService(env, userService);
        const sent = await sendLoop({ env, clientId, api });
        debug('send success?', sent);
        sent && onSend();
      } finally {
        setButtonDisabled(false);
      }
    },
    [env, clientId, overlayContext, onSend],
  );

  const dismissClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();

      overlayContext('dismissInfoOverlay');
    },
    [overlayContext],
  );

  if (isViewInReadMode()) {
    return null;
  }

  const classes = classnames('send-and-track__button', {
    'send-and-track__button--overlay': showSendInfoOverlay,
  });

  const button = (
    <>
      {!buttonDisabled && (
        <Button
          className={classes}
          color="trafficCone"
          label="Send + Track"
          onClick={sendClick}
          rectangle
          size="medium"
          type="button"
        />
      )}
      {buttonDisabled && (
        <div className="send-and-track__sending">
          <LoadingDots size={1.25} color="traffic-cone" />
        </div>
      )}
    </>
  );

  if (showSendInfoOverlay) {
    return (
      <>
        <div className="send-and-track__overlay" onClick={dismissClick} />
        {button}
        <div className="send-and-track__info">
          <SendAirplane height="17" width="17" title="Send Envelope" fill="#ff6600" />
          <h1 className="send-and-track__heading">Ready to Send?</h1>
          <p className="send-and-track__copy">
            Make sure you <strong>click this button every time</strong> you want BombBomb to send and track
            your emails and videos.
          </p>
          <a className="send-and-track__link" href="#" onClick={dismissClick}>
            Got it!
          </a>
        </div>
      </>
    );
  }

  return button;
}
